import React from "react";
import { cva, type VariantProps } from "cva";
import { TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card } from "../../../framing/card";
import { cn } from "../../../lib/utils";
import type { OptionalTenantProp } from "../../../../types";

const baseFormRootErrorVariants = cva({
  base: "text-[0.8rem] font-medium text-destructive text-center",
  variants: {
    variant: {
      default: undefined,
      card: "text-primary",
    },
    size: {
      default: undefined,
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export interface BaseFormRootErrorProps
  extends React.HTMLAttributes<HTMLParagraphElement>,
    OptionalTenantProp {
  as?: React.ElementType;
  withIcon?: boolean;
  keyPrefix?: string;
  rootError?: string;
  cardHeaderClassname?: string;
  variant?: VariantProps<typeof baseFormRootErrorVariants>["variant"];
  size?: VariantProps<typeof baseFormRootErrorVariants>["size"];
}

const BaseFormRootError = React.forwardRef<
  HTMLParagraphElement,
  BaseFormRootErrorProps
>(
  (
    {
      as: Component = "p",
      className,
      cardHeaderClassname,
      variant,
      size,
      withIcon,
      keyPrefix = "common",
      rootError,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation("errors", { keyPrefix });

    if (!rootError || rootError.trim() === "") return null;

    const translatedErrorMessage = t(String(rootError));

    switch (variant) {
      case "card":
        return (
          <Card
            className={cn(
              baseFormRootErrorVariants({ variant, size }),
              className,
            )}
            ref={ref}
            {...props}
          >
            <Card.Header
              className={cn(
                "flex-row items-center space-x-2 px-2 py-1.5 sm:space-x-4 sm:px-3 sm:py-2",
                cardHeaderClassname,
              )}
            >
              <TriangleAlert className="size-14 min-h-14 min-w-14" />
              <div className="space-y-1">
                <Card.Title className="text-sm!">
                  {t("letsTryAgain")}
                </Card.Title>
                <Card.Description className="text-sm!">
                  {translatedErrorMessage}
                </Card.Description>
              </div>
            </Card.Header>
          </Card>
        );
      default:
        return (
          <Component
            className={cn(
              baseFormRootErrorVariants({ variant, size }),
              className,
            )}
            ref={ref}
            {...props}
          >
            {translatedErrorMessage}
          </Component>
        );
    }
  },
);

export { BaseFormRootError };
